/**
 * Generated by `cargo test -p videas-api --test contracts`, which `bun run api:sync` runs.
 * Do not edit by hand: the next run reverts the patch.
 *
 * Every permission key the backend declares, one union per scope it is held at, the ones an
 * API key may carry, and the feature flags `GET /api/app/init` and `GET /api/app/access` may
 * list. A key checked against another scope's set is a type error rather than an affordance
 * hidden forever.
 */

/** Held across one organization, through a membership: the effective set `GET /api/app/access` answers in `org.permissions`, and what `session.can` checks. */
export const OrgPermissions = {
  /** Access the organization-wide Analytics app */
  ANALYTICS_APP_ACCESS: 'analytics.app.access',
  /** Read the organization's playback sessions through the public API: which viewer watched which asset, when, and how much */
  ANALYTICS_SESSIONS_READ: 'analytics.sessions.read',
  /** Edit the organization's library through the public API: an asset's words, its tags, its custom fields and its picture */
  ASSETS_LIBRARY_EDIT: 'assets.library.edit',
  /** Export the whole organization's library, every workspace included, and read the organization's export log */
  ASSETS_LIBRARY_EXPORT: 'assets.library.export',
  /** Read the organization's library through the public API: list its assets, their descriptions and the workspaces they are in */
  ASSETS_LIBRARY_READ: 'assets.library.read',
  /** Add files to the organization's library through the public API: send them, or have them fetched from a link */
  ASSETS_LIBRARY_UPLOAD: 'assets.library.upload',
  /** Import a video hosted elsewhere — YouTube, Vimeo, Dailymotion or an accepted player — into the library */
  ASSETS_THIRD_PARTY_VIDEO_IMPORT: 'assets.third_party_video.import',
  /** Mint, list and revoke the organization's API keys */
  AUTH_API_KEYS_MANAGE: 'auth.api_keys.manage',
  /** Invite people into the organization */
  AUTH_MEMBERS_INVITE: 'auth.members.invite',
  /** Manage members: change a member's role, remove a member */
  AUTH_MEMBERS_MANAGE: 'auth.members.manage',
  /** Delete the organization and everything scoped to it */
  AUTH_ORG_DELETE: 'auth.org.delete',
  /** Transfer the organization to another member */
  AUTH_ORG_TRANSFER: 'auth.org.transfer',
  /** Define, edit and delete the organization's own roles */
  AUTH_ROLES_MANAGE: 'auth.roles.manage',
  /** Create, edit and retire the organization's ONISEP partners */
  ONISEP_PARTNERS_MANAGE: 'onisep.partners.manage',
  /** Access the Scribe (subtitles) app */
  SUBTITLES_SCRIBE_ACCESS: 'subtitles.scribe.access',
  /** Manage billing */
  TENANCY_BILLING_MANAGE: 'tenancy.billing.manage',
  /** View and manage every pending invitation and invitation link in the organization, not only the ones the caller created */
  TENANCY_INVITATIONS_MANAGE_ALL: 'tenancy.invitations.manage_all',
  /** Read the organization's registration requests, approve or reject them, and choose to be told when one arrives */
  TENANCY_MEMBERSHIP_REQUESTS_REVIEW: 'tenancy.membership_requests.review',
  /** Manage organization settings */
  TENANCY_SETTINGS_MANAGE: 'tenancy.settings.manage',
  /** Create new workspaces in this organization */
  TENANCY_WORKSPACES_CREATE: 'tenancy.workspaces.create',
  /** Administer every workspace, private ones included, without being a member of it */
  TENANCY_WORKSPACES_MANAGE_ALL: 'tenancy.workspaces.manage_all',
  /** View every workspace in the organization, including private ones */
  TENANCY_WORKSPACES_VIEW_ALL: 'tenancy.workspaces.view_all',
  /** View public workspaces in the organization */
  TENANCY_WORKSPACES_VIEW_PUBLIC: 'tenancy.workspaces.view_public',
  /** Publish and unpublish a channel's videos, and organise its categories */
  WEBSITES_CATALOGUE_EDIT: 'websites.catalogue.edit',
  /** Publish the organization's videos on its channels through the public API: create a publication, set its status and its address, and file it in the channel's categories */
  WEBSITES_CATALOGUE_PUBLISH: 'websites.catalogue.publish',
  /** Read the organization's channels through the public API: the channels, their categories and what each one publishes */
  WEBSITES_CATALOGUE_READ: 'websites.catalogue.read',
  /** Review, answer and remove the comments left on a channel */
  WEBSITES_COMMENTS_MODERATE: 'websites.comments.moderate',
  /** Grant and revoke access to a channel's videos, categories and groups */
  WEBSITES_GRANTS_MANAGE: 'websites.grants.manage',
  /** Add, approve, suspend, invite and remove a channel's members */
  WEBSITES_MEMBERS_MANAGE: 'websites.members.manage',
  /** View a channel's members and what each of them can reach */
  WEBSITES_MEMBERS_VIEW: 'websites.members.view',
  /** Edit a channel's pages, the content blocks on them and its navigation menus */
  WEBSITES_PAGES_EDIT: 'websites.pages.edit',
  /** View a channel's audience figures: views, members, sources and top content */
  WEBSITES_STATS_VIEW: 'websites.stats.view',
  /** Set a channel's prices and subscription plans, and what each plan includes */
  WEBSITES_STOREFRONT_EDIT: 'websites.storefront.edit',
  /** Handle what a channel's visitors send in: access requests, contact messages and deposits */
  WEBSITES_SUBMISSIONS_MANAGE: 'websites.submissions.manage',
  /** Create channels */
  WEBSITES_WEBSITE_CREATE: 'websites.website.create',
  /** Delete a channel, together with everything published on it */
  WEBSITES_WEBSITE_DELETE: 'websites.website.delete',
  /** Manage a channel's settings: its name, its look, its sign-in policy and its domains */
  WEBSITES_WEBSITE_MANAGE: 'websites.website.manage',
} as const

export type OrgPermission = (typeof OrgPermissions)[keyof typeof OrgPermissions]

/** Held inside one workspace, through a seat: what a workspace role declares (`GET /api/app/workspaces/{workspace_id}/roles/{role_key}`). Never in the organization's set. */
export const WorkspacePermissions = {
  /** Create and upload assets */
  ASSETS_ASSET_CREATE: 'assets.asset.create',
  /** Permanently delete assets */
  ASSETS_ASSET_DELETE: 'assets.asset.delete',
  /** Edit asset metadata */
  ASSETS_ASSET_EDIT: 'assets.asset.edit',
  /** Export asset data */
  ASSETS_ASSET_EXPORT: 'assets.asset.export',
  /** Approve or reject moderated assets */
  ASSETS_ASSET_MODERATE: 'assets.asset.moderate',
  /** Share assets */
  ASSETS_ASSET_SHARE: 'assets.asset.share',
  /** Move assets to trash and restore them */
  ASSETS_ASSET_TRASH: 'assets.asset.trash',
  /** View an asset's analytics */
  ASSETS_ASSET_VIEW_ANALYTICS: 'assets.asset.view_analytics',
  /** Create collections */
  ASSETS_COLLECTION_CREATE: 'assets.collection.create',
  /** Delete collections */
  ASSETS_COLLECTION_DELETE: 'assets.collection.delete',
  /** Edit collections and their contents */
  ASSETS_COLLECTION_EDIT: 'assets.collection.edit',
  /** Invite members to this workspace and manage its invitation links */
  ASSETS_MEMBERS_INVITE: 'assets.members.invite',
  /** Change workspace member roles and remove members */
  ASSETS_MEMBERS_MANAGE: 'assets.members.manage',
  /** Delete an asset's subtitles */
  ASSETS_SUBTITLE_DELETE: 'assets.subtitle.delete',
  /** Create, generate, import and edit an asset's subtitles */
  ASSETS_SUBTITLE_EDIT: 'assets.subtitle.edit',
  /** Delete this workspace */
  ASSETS_WORKSPACE_DELETE: 'assets.workspace.delete',
  /** Manage workspace settings */
  ASSETS_WORKSPACE_MANAGE: 'assets.workspace.manage',
} as const

export type WorkspacePermission = (typeof WorkspacePermissions)[keyof typeof WorkspacePermissions]

/** Held over the platform, through a staff seat: what `GET /api/app/access` answers in `staff.permissions`. Never in any organization's set. */
export const StaffPermissions = {
  /** Operate the platform: the staff surface above every organization */
  AUTH_PLATFORM_OPERATE: 'auth.platform.operate',
  /** Read the console's access journal: which operator read what, when and from where */
  STAFF_ACCESSES_VIEW: 'staff.accesses.view',
  /** Browse the platform's accounts: who they are, how they sign in, where they belong */
  STAFF_ACCOUNTS_VIEW: 'staff.accounts.view',
  /** Approve or reject reported user content */
  STAFF_ASSETS_MODERATE: 'staff.assets.moderate',
  /** Browse every asset, not only those pending moderation */
  STAFF_ASSETS_VIEW_ALL: 'staff.assets.view_all',
  /** Overrule the category of one of the platform's organizations, with a reason, or remove the override */
  STAFF_CLASSIFICATIONS_MANAGE: 'staff.classifications.manage',
  /** Decide what an email domain is for the whole installation: junk or legit, overruling its class */
  STAFF_EMAIL_DOMAINS_MANAGE: 'staff.email_domains.manage',
  /** Upload and delete platform files through the administrative endpoints */
  STAFF_FILES_MANAGE: 'staff.files.manage',
  /** Dress a channel's mail: the templates its messages wear, and the address its campaigns go out from */
  STAFF_MAIL_MANAGE: 'staff.mail.manage',
  /** Invite, edit or revoke staff members and their roles */
  STAFF_MEMBERS_MANAGE: 'staff.members.manage',
  /** Browse the moderation audit trail */
  STAFF_MODERATION_LOG_VIEW: 'staff.moderation_log.view',
  /** Write notes and tags on the platform's accounts and organizations, and keep its vocabulary of tags */
  STAFF_NOTES_MANAGE: 'staff.notes.manage',
  /** Queue again the media work the queue gave up on: one job, or every job of one cause */
  STAFF_OPERATIONS_MANAGE: 'staff.operations.manage',
  /** Read the installation's machinery: its queues, its outbound mail, its sweeps and the legacy drain */
  STAFF_OPERATIONS_VIEW: 'staff.operations.view',
  /** Browse the platform's organizations: their state, members, wallet and storage */
  STAFF_ORGANIZATIONS_VIEW: 'staff.organizations.view',
  /** Browse the platform's payments: who paid, for which organization, and their invoices */
  STAFF_PAYMENTS_VIEW: 'staff.payments.view',
  /** Platform statistics: assets, users, storage, growth */
  STAFF_PLATFORM_STATS_VIEW: 'staff.platform_stats.view',
  /** Write one organization's own prices and the basis its video storage is billed on */
  STAFF_PRICING_MANAGE: 'staff.pricing.manage',
  /** Read the platform's paid credit: consumed day by day, held, sold, and the forecast drawn from them */
  STAFF_REVENUE_VIEW: 'staff.revenue.view',
  /** Read and write the staff's tasks: what is to be done, for which account or organization, by when and by whom */
  STAFF_TASKS_MANAGE: 'staff.tasks.manage',
} as const

export type StaffPermission = (typeof StaffPermissions)[keyof typeof StaffPermissions]

/** The organization keys an API key may carry — a subset of `OrgPermissions`, not a scope of its own: what `POST /api/app/orgs/current/api-keys` accepts in `permissions`, each also held to what the minting member's own role holds. */
export const ApiKeyPermissions = {
  /** Read the organization's playback sessions through the public API: which viewer watched which asset, when, and how much */
  ANALYTICS_SESSIONS_READ: 'analytics.sessions.read',
  /** Edit the organization's library through the public API: an asset's words, its tags, its custom fields and its picture */
  ASSETS_LIBRARY_EDIT: 'assets.library.edit',
  /** Read the organization's library through the public API: list its assets, their descriptions and the workspaces they are in */
  ASSETS_LIBRARY_READ: 'assets.library.read',
  /** Add files to the organization's library through the public API: send them, or have them fetched from a link */
  ASSETS_LIBRARY_UPLOAD: 'assets.library.upload',
  /** Import a video hosted elsewhere — YouTube, Vimeo, Dailymotion or an accepted player — into the library */
  ASSETS_THIRD_PARTY_VIDEO_IMPORT: 'assets.third_party_video.import',
  /** Publish the organization's videos on its channels through the public API: create a publication, set its status and its address, and file it in the channel's categories */
  WEBSITES_CATALOGUE_PUBLISH: 'websites.catalogue.publish',
  /** Read the organization's channels through the public API: the channels, their categories and what each one publishes */
  WEBSITES_CATALOGUE_READ: 'websites.catalogue.read',
} as const

export type ApiKeyPermission = (typeof ApiKeyPermissions)[keyof typeof ApiKeyPermissions]

/** The feature flags a client may be told about. `GET /api/app/init` lists the ones enabled on the platform, `GET /api/app/access` the ones enabled for the organization in `org.features`; a key absent from a list is off there. */
export const PublicFlags = {
  /** Library exports: a workspace's or the whole organization's catalogue taken out as a spreadsheet, and the presets that configure one. Set at organization scope only */
  ASSETS_LIBRARY_EXPORTS: 'assets.library_exports',
  /** Slug addresses: the previous platform's addresses naming an asset by its slug resolve to this organization's assets. Set at organization scope only */
  ASSETS_SLUG_ADDRESSES: 'assets.slug_addresses',
  /** Source links: an asset's current source file is reachable at /sources/{uid} by anybody holding the address, with no account or signature */
  ASSETS_SOURCE_LINKS: 'assets.source_links',
  /** Third-party videos: a YouTube, Vimeo or Dailymotion address, or an accepted host's embed code, pasted into the library as an asset that plays that player */
  ASSETS_THIRD_PARTY_VIDEOS: 'assets.third_party_videos',
  /** Integration grants: a third-party integration may be granted an API key through the connect handshake */
  AUTH_INTEGRATION_GRANTS: 'auth.integration_grants',
  /** Password sign-in: login, password reset and password change are enabled */
  AUTH_PASSWORD_LOGIN: 'auth.password_login',
  /** Open self-registration: the signup endpoint is enabled */
  AUTH_PUBLIC_SIGNUP: 'auth.public_signup',
  /** A second factor is required: an account carrying none is refused a session until it enrols one */
  AUTH_REQUIRE_MFA: 'auth.require_mfa',
  /** Self-service organizations: a signed-in user may create their own org */
  AUTH_SELF_SERVICE_ORGS: 'auth.self_service_orgs',
  /** Clips: short videos cut out of the organization's own — the analysis, the editor and the export */
  CLIPS_ENABLED: 'clips.enabled',
  /** Asset-direct embeds: an asset of the organization is embeddable at /embed/{uid} by its own uid, with no share or native embed */
  EMBEDS_ASSET_DIRECT: 'embeds.asset_direct',
  /** The ONISEP integration: the IDEO catalogue, the partners, and the mirror of eligible videos and video collections into ONISEP's catalogue. Set at organization scope only */
  ONISEP_ENABLED: 'onisep.enabled',
  /** Voices detected on demand: Scribe's Speakers tab can tell a track's speakers apart and give each line its voice */
  SUBTITLES_DIARISATION: 'subtitles.diarisation',
  /** The glossary heard by the transcription: the organization's active glossary terms are handed to the speech model with its prompt */
  SUBTITLES_TRANSCRIPT_GLOSSARY: 'subtitles.transcript_glossary',
  /** Transcripts shaped by the organization's settings: reading speed, cue durations, lines and characters per line applied to every new transcription */
  SUBTITLES_TRANSCRIPT_SHAPING: 'subtitles.transcript_shaping',
  /** Channels: the operator's side of a channel — its list, creation, settings, catalogue, pages, members, storefront and statistics */
  WEBSITES_ENABLED: 'websites.enabled',
} as const

export type PublicFlag = (typeof PublicFlags)[keyof typeof PublicFlags]
